import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DraftCard from "@/components/DraftCard";
import CustomPostDialog from "@/components/CustomPostDialog";
import GenerateDialog from "@/components/GenerateDialog";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2 } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Pending" },
  { id: "scheduled", label: "Scheduled" },
  { id: "posted", label: "Posted" },
  { id: "rejected", label: "Rejected" },
];

export default function Queue() {
  const [drafts, setDrafts] = useState([]);
  const [clicksByDraft, setClicksByDraft] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  const refresh = async () => {
    const [draftsRes, clicksRes] = await Promise.all([
      api.get("/drafts"),
      api.get("/promo-clicks").catch(() => ({ data: { by_draft: {} } })),
    ]);
    setDrafts(draftsRes.data);
    setClicksByDraft(clicksRes.data?.by_draft || {});
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  // Auto-refresh every 20s while any draft has a rendering AI video.
  useEffect(() => {
    const hasRendering = drafts.some((d) => d.video_status === "rendering");
    if (!hasRendering) return;
    const id = setInterval(refresh, 20000);
    return () => clearInterval(id);
  }, [drafts]);

  const pollUntilDone = async ({ job_id, total }) => {
    setGenerating(true);
    let done = false;
    for (let i = 0; i < 80 && !done; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const s = await api.get(`/drafts/generate/${job_id}`);
        await refresh();
        if (s.data.status === "completed") {
          done = true;
          toast.success(`Generated ${s.data.done}/${total} drafts.`);
        } else if (s.data.status === "failed") {
          done = true;
          toast.error("Generation failed.");
        }
      } catch {/* transient */}
    }
    setGenerating(false);
  };

  const generate = () => setGenOpen(true);

  const clearRejected = async () => {
    const count = drafts.filter((d) => d.status === "rejected").length;
    if (count === 0) {
      toast("No rejected drafts to clear.");
      return;
    }
    if (!window.confirm(`Permanently delete ${count} rejected draft${count === 1 ? "" : "s"}?`)) return;
    try {
      const r = await api.delete("/drafts/bulk/rejected");
      toast.success(`Deleted ${r.data.deleted} rejected draft${r.data.deleted === 1 ? "" : "s"}.`);
      await refresh();
    } catch {
      toast.error("Failed to clear rejected drafts");
    }
  };

  const filtered = filter === "all" ? drafts : drafts.filter((d) => d.status === filter);
  const rejectedCount = drafts.filter((d) => d.status === "rejected").length;

  return (
    <div className="fade-in" data-testid="queue-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="label-mono mb-4">Approval Queue</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
            Approve what <span className="italic">goes live.</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className="btn-ghost-line"
            onClick={() => setCustomOpen(true)}
            data-testid="custom-post-open-btn"
          >
            <Plus size={14} /> Custom Post
          </button>
          <button
            className="btn-amber"
            onClick={generate}
            disabled={generating}
            data-testid="queue-generate-btn"
          >
            <Sparkles size={14} />
            {generating ? "Generating…" : "Generate Batch"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 items-center">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`label-mono px-4 py-2 border transition-colors ${
              filter === f.id
                ? "border-[#FF3B00] text-white bg-[#FF3B00]/10"
                : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
            }`}
          >
            {f.label} {f.id !== "all" && (
              <span className="ml-1 text-white/40">
                ({drafts.filter((d) => d.status === f.id).length})
              </span>
            )}
          </button>
        ))}
        {rejectedCount > 0 && (filter === "rejected" || filter === "all") && (
          <button
            onClick={clearRejected}
            data-testid="clear-rejected-btn"
            className="ml-auto label-mono px-3 py-2 border border-red-500/30 text-red-400 hover:text-red-300 hover:border-red-500/50 transition-colors flex items-center gap-2"
          >
            <Trash2 size={12} /> Clear {rejectedCount} rejected
          </button>
        )}
      </div>

      {loading ? (
        <div className="label-mono">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="panel p-16 text-center">
          <div className="label-mono mb-3">Empty</div>
          <p className="text-white/55">
            {drafts.length === 0
              ? "No drafts yet. Generate your first weekly batch above."
              : "Nothing matches this filter."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 fade-in-stagger">
          {filtered.map((d) => (
            <DraftCard
              key={d.draft_id}
              draft={d}
              clicks={clicksByDraft[d.draft_id] || 0}
              onChange={refresh}
            />
          ))}
        </div>
      )}

      <CustomPostDialog open={customOpen} onOpenChange={setCustomOpen} onCreated={refresh} />
      <GenerateDialog open={genOpen} onOpenChange={setGenOpen} onStarted={pollUntilDone} />
    </div>
  );
}
